import { forwardRef } from 'react';
import styled, { css, RuleSet } from 'styled-components';
import { BORDER_RADIUS, COLOURS, getBodyFont, isFocused, shouldForwardProp, SPACES, width } from '@belong/themes';
import { ColoursValue, ITestable, StyledCSS } from '@belong/types';
import { useFormLoading } from '../GenericFormLayout/FormLoadingProvider';
import { TextInputUnstyled } from '../TextInputUnstyled';
import { ITextInput } from './TextInput.types';

export const baseStyles = css`
  /* Minus 0.1rem to cater for the border */
  padding: calc(${SPACES.small}rem - 0.1rem) calc(${SPACES.medium}rem - 0.1rem);
  border: 0.1rem solid;
  border-radius: ${BORDER_RADIUS.INPUT};
  transition:
    0.2s background-color ease,
    0.2s border-color ease;
`;

const extractProps = (props: ITextInput) => ({ type: props.type || 'text' });
export const TextInputWrapper = styled.div<Pick<ITextInput, 'inputSymbolPrefix'> & ITestable>`
  ${props =>
    props.inputSymbolPrefix &&
    css`
      position: relative;
      display: block;
      &:before {
        content: '${props.inputSymbolPrefix}';
        position: absolute;
        left: 10px;
        top: 50%;
        transform: translateY(-50%);
      }
    `}
`;

interface IVariation {
  backgroundColor: ColoursValue;
  borderColor: ColoursValue;
  color: ColoursValue;
  focus?: {
    backgroundColor: ColoursValue;
  };
  isError: {
    backgroundColor: ColoursValue;
    borderColor: ColoursValue;
    color?: ColoursValue;
  };
}

export type VariationTypes = 'default' | 'onDarkBG';

export const colourVariations: Record<VariationTypes, IVariation> = {
  default: {
    backgroundColor: COLOURS.GREY_100,
    borderColor: COLOURS.GREY_600,
    color: COLOURS.BLACK,
    isError: {
      backgroundColor: COLOURS.LIGHT_RED,
      borderColor: COLOURS.DARK_RED
    }
  },
  onDarkBG: {
    backgroundColor: COLOURS.GREY_700,
    borderColor: COLOURS.GREY_400,
    color: COLOURS.GREY_400,
    isError: {
      backgroundColor: COLOURS.GREY_700,
      borderColor: COLOURS.ALT_RED,
      color: COLOURS.LIGHT_RED
    }
  }
};

type GetStyleProp = Pick<ITextInput, 'width' | 'isError' | 'onDarkBG'>;
export const getBaseInputStyles = (props: GetStyleProp): RuleSet => {
  const variant = props.onDarkBG ? colourVariations.onDarkBG : colourVariations.default;
  return css`
    ${getBodyFont('medium')}
    ${baseStyles}
    ${width};

    background-color: ${variant.backgroundColor};
    border-color: ${variant.borderColor};
    color: ${variant.color};
    display: block;
    max-width: 100%;
    text-align: left;

    &:focus {
      ${variant.focus && `background-color: ${variant.focus.backgroundColor}`};
      ${isFocused()};
    }

    // prevent safari autofill styles
    &::-webkit-contacts-auto-fill-button,
    &::-webkit-credentials-auto-fill-button {
      visibility: hidden;
      display: none !important;
      pointer-events: none;
      height: 0;
      width: 0;
      margin: 0;
    }

    ${props.isError &&
    css`
      &,
      &:hover {
        background-color: ${variant.isError.backgroundColor};
        border-color: ${variant.isError.borderColor};
        ${variant.isError.color && `color: ${variant.isError.color}`};
      }
    `}
  `;
};

/**
 * Callers must omit `type` to use this attrs default; explicitly passing `undefined` resets it.
 */
export const StyledTextInput = styled(TextInputUnstyled)
  .withConfig({
    shouldForwardProp: shouldForwardProp<ITextInput>('onDarkBG')
  })
  .attrs<ITextInput>(extractProps)<ITextInput>`
  ${getBaseInputStyles}

  ${(props: ITextInput): StyledCSS<ITextInput> => {
    const variant = props.onDarkBG ? colourVariations.onDarkBG : colourVariations.default;

    return css`
      ${props.isFocused &&
      css`
        ${variant.focus && `background-color: ${variant.focus.backgroundColor}`};
        ${isFocused()};
      `}

      ${!!props.inputSymbolPrefix &&
      css`
        padding-left: calc(${SPACES.xxsmall}rem + ${SPACES.medium}rem);
      `}
    `;
  }}
`;

export const TextInput = forwardRef<HTMLInputElement, ITextInput>((props, ref) => {
  const { isLoading } = useFormLoading();
  const isReadOnly = !!props.readOnly || isLoading;

  return <StyledTextInput {...props} ref={ref} readOnly={isReadOnly} />;
});

TextInput.displayName = 'TextInput';
